import { ArrowLeftOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Col, Flex, Row, Spin, Tabs, Typography, message } from "antd";
import type { TabsProps } from "antd";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useIsMobileView } from "../../hooks/ui-hook";
import {
  type ClinicianClient,
  type ClinicianNote,
  type ClinicianProfileData,
  membersService,
} from "../../service/members.service";
import ClinicianClients from "./ClinicianClients.component";
import ClinicianNotes from "./ClinicianNotes.component";
import styles from "./clinician-profile.module.scss";

const { Text, Title } = Typography;

const ClinicianProfile: React.FC = () => {
  const { userUuid } = useParams<{ userUuid: string }>();
  const navigate = useNavigate();
  const isMobileView = useIsMobileView();

  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState<ClinicianProfileData | null>(null);
  const [activeTab, setActiveTab] = useState("clients");
  const [clientsPage, setClientsPage] = useState(1);
  const [notesPage, setNotesPage] = useState(1);
  const [selectedClientUuid, setSelectedClientUuid] = useState<string | undefined>(undefined);
  const [clients, setClients] = useState<ClinicianClient[]>([]);
  const [notes, setNotes] = useState<ClinicianNote[]>([]);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [notesTotal, setNotesTotal] = useState(0);

  const fetchProfile = useCallback(async () => {
    if (!userUuid) return;

    try {
      setLoading(true);
      const data = await membersService.getClinicianProfile(userUuid, clientsPage, notesPage, selectedClientUuid);
      setProfileData(data);
      setClients(data.clients.results);
      setNotes(data.notes.results);
      setClientsTotal(data.clients.count);
      setNotesTotal(data.notes.count);
    } catch (error: any) {
      console.error("Error fetching clinician profile:", error);
      if (error?.response?.status === 403) {
        message.error("You don't have permission to view this clinician's profile");
        navigate("/members");
      } else if (error?.response?.status === 404) {
        message.error("Clinician not found");
        navigate("/members");
      } else {
        message.error("Failed to load clinician profile");
      }
    } finally {
      setLoading(false);
    }
  }, [userUuid, clientsPage, notesPage, selectedClientUuid, navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleClientFilterChange = (clientUuid: string | undefined) => {
    setSelectedClientUuid(clientUuid);
    setNotesPage(1); // Reset to first page when filtering
  };

  const handleClientsPageChange = (page: number) => {
    setClientsPage(page);
  };

  const handleNotesPageChange = (page: number) => {
    setNotesPage(page);
  };

  if (loading || !profileData) {
    return (
      <Flex justify="center" align="center" style={{ height: "100vh" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  const { clinician } = profileData;

  const items: TabsProps["items"] = [
    {
      key: "clients",
      label: `Clients (${clientsTotal})`,
      children: (
        <ClinicianClients
          clients={clients}
          total={clientsTotal}
          currentPage={clientsPage}
          onPageChange={handleClientsPageChange}
          loading={loading}
        />
      ),
    },
    {
      key: "notes",
      label: `Notes (${notesTotal})`,
      children: (
        <ClinicianNotes
          notes={notes}
          total={notesTotal}
          currentPage={notesPage}
          onPageChange={handleNotesPageChange}
          loading={loading}
          selectedClientUuid={selectedClientUuid}
          onClientFilterChange={handleClientFilterChange}
          clients={clients}
        />
      ),
    },
  ];

  return (
    <Row className={styles.clinicianProfile}>
      <Col span={24}>
        {/* Breadcrumb */}
        <Flex align="center" gap={16} className={styles.clinicianProfile__breadcrumb}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/members")}
            className={styles.clinicianProfile__backButton}
          />
          <Breadcrumb
            items={[
              {
                title: (
                  <span style={{ cursor: "pointer" }} onClick={() => navigate("/members")}>
                    Members
                  </span>
                ),
              },
              {
                title: clinician.name,
              },
            ]}
          />
        </Flex>

        {/* Clinician Info Header */}
        <Card className={styles.clinicianProfile__header}>
          <Flex vertical={isMobileView} justify="space-between" align={isMobileView ? "stretch" : "center"} gap={16}>
            <Flex vertical gap={8}>
              <Title level={3} style={{ margin: 0, textAlign: "left" }}>
                {clinician.name}
              </Title>
              <Text type="secondary" style={{ textAlign: "left" }}>
                {clinician.email}
              </Text>
              <Flex gap={16} wrap="wrap">
                <Text>
                  <strong>Role:</strong> {clinician.role.charAt(0).toUpperCase() + clinician.role.slice(1)}
                </Text>
                {clinician.practice_area_label && (
                  <Text>
                    <strong>Practice Area:</strong> {clinician.practice_area_label}
                  </Text>
                )}
                {clinician.specialty_label && (
                  <Text>
                    <strong>Specialty:</strong> {clinician.specialty_label}
                  </Text>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Card>

        {/* Tabs for Clients and Notes */}
        <div className={styles.clinicianProfile__content}>
          <Tabs activeKey={activeTab} items={items} onChange={setActiveTab} />
        </div>
      </Col>
    </Row>
  );
};

export default ClinicianProfile;
